import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { buildApiUrl, STORAGE_KEYS, HTTP_CONFIG } from '../constants/api.constants';

/**
 * Base HTTP service that provides common functionality for all API calls
 * Handles authentication, error handling, and request configuration
 */
@Injectable({
  providedIn: 'root'
})
export class BaseHttpService {
  // `inject()` használata constructor helyett, hogy a leszármazott osztályok
  // konstruktorát ne kelljen módosítani (20+ helyen van extend-elve)
  private readonly router = inject(Router);
  private isRedirectingToLogin = false;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) protected platformId: Object
  ) {}

  /**
   * Gets the authentication token from localStorage
   */
  protected getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(STORAGE_KEYS.TOKEN);
    }
    return null;
  }

  /**
   * Creates HTTP headers with authentication token
   */
  protected createHeaders(additionalHeaders: Record<string, string> = {}): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': HTTP_CONFIG.HEADERS.CONTENT_TYPE,
      'Accept': HTTP_CONFIG.HEADERS.ACCEPT,
      ...additionalHeaders
    });

    const token = this.getToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Handles HTTP errors in a consistent way.
   * `callerStack`: szinkron Error a metódus hívási pontján (pl. auth-core.service.ts:132)
   */
  protected handleError = (callerStack?: Error) => (error: any) => {
    // Csatoljuk a hívási helyen rögzített Error-t a HTTP hibához (a GlobalErrorHandler olvassa)
    if (callerStack && error !== null && typeof error === 'object') {
      (error as Record<string, unknown>)['_capturedError'] = callerStack;
    }
    console.error('HTTP Error:', error);
    
    let errorMessage = 'Ismeretlen hiba történt';
    
    if (error.error?.detail) {
      errorMessage = error.error.detail;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error.error === 'string') {
      errorMessage = error.error;
    }

    // Handle specific HTTP status codes
    switch (error.status) {
      case 401:
        errorMessage = 'Nincs jogosultság a művelethez';
        break;
      case 403:
        if (!errorMessage || errorMessage === 'Ismeretlen hiba történt') {
          errorMessage = 'Hozzáférés megtagadva';
        }
        break;
      case 404:
        errorMessage = 'A kért erőforrás nem található';
        break;
      case 500:
        errorMessage = 'Szerver hiba történt';
        break;
      case 0:
        errorMessage = 'Nincs kapcsolat a szerverrel';
        break;
    }

    return throwError(() => new Error(errorMessage));
  };

  /**
   * Checks if the user has a valid authentication token
   */
  protected hasValidToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  /**
   * Makes a GET request with authentication
   */
  protected get<T>(endpoint: string, params?: HttpParams): Observable<T> {
    const callerStack = new Error('HTTP GET ' + endpoint);
    const url = buildApiUrl(endpoint);
    const headers = this.createHeaders();

    return this.http.get<T>(url, { headers, params })
      .pipe(
        retry({
          count: 3,
          delay: (error, retryCount) => {
            // Ha nincs internet, ne próbáld újra
            if (!navigator.onLine || error.status === 0) {
              throw error;
            }
            // 4xx kliens hibáknál ne próbáld újra (401, 403, 404, stb.)
            if (error.status >= 400 && error.status < 500) {
              throw error;
            }
            // Exponenciális backoff: 1s, 2s, 4s
            return timer(Math.pow(2, retryCount - 1) * 1000);
          }
        }),
        catchError(this.handleError(callerStack))
      );
  }

  /**
   * Makes a POST request with authentication
   */
  protected post<T>(endpoint: string, data: any): Observable<T> {
    const callerStack = new Error('HTTP POST ' + endpoint);
    const url = buildApiUrl(endpoint);
    const headers = this.createHeaders();

    return this.http.post<T>(url, data, { headers })
      .pipe(
        retry({
          count: 3,
          delay: (error, retryCount) => {
            // Ha nincs internet, ne próbáld újra
            if (!navigator.onLine || error.status === 0) {
              throw error;
            }
            // 4xx kliens hibáknál ne próbáld újra (401, 403, 404, stb.)
            if (error.status >= 400 && error.status < 500) {
              throw error;
            }
            // Exponenciális backoff: 1s, 2s, 4s
            return timer(Math.pow(2, retryCount - 1) * 1000);
          }
        }),
        catchError(this.handleError(callerStack))
      );
  }

  /**
   * Makes a POST request that returns a binary file (pl. XLSX export)
   */
  protected postBlob(endpoint: string, data: any): Observable<Blob> {
    const callerStack = new Error('HTTP POST (blob) ' + endpoint);
    const url = buildApiUrl(endpoint);
    const headers = this.createHeaders();

    return this.http.post(url, data, { headers, responseType: 'blob' })
      .pipe(catchError(this.handleError(callerStack)));
  }

  /**
   * Makes a PUT request with authentication
   */
  protected put<T>(endpoint: string, data: any): Observable<T> {
    const callerStack = new Error('HTTP PUT ' + endpoint);
    const url = buildApiUrl(endpoint);
    const headers = this.createHeaders();

    return this.http.put<T>(url, data, { headers })
      .pipe(
        retry({
          count: 3,
          delay: (error, retryCount) => {
            // Ha nincs internet, ne próbáld újra
            if (!navigator.onLine || error.status === 0) {
              throw error;
            }
            // 4xx kliens hibáknál ne próbáld újra (401, 403, 404, stb.)
            if (error.status >= 400 && error.status < 500) {
              throw error;
            }
            // Exponenciális backoff: 1s, 2s, 4s
            return timer(Math.pow(2, retryCount - 1) * 1000);
          }
        }),
        catchError(this.handleError(callerStack))
      );
  }

  /**
   * Makes a DELETE request with authentication
   */
  protected delete<T>(endpoint: string): Observable<T> {
    const callerStack = new Error('HTTP DELETE ' + endpoint);
    const url = buildApiUrl(endpoint);
    const headers = this.createHeaders();

    return this.http.delete<T>(url, { headers })
      .pipe(
        retry({
          count: 3,
          delay: (error, retryCount) => {
            // Ha nincs internet, ne próbáld újra
            if (!navigator.onLine || error.status === 0) {
              throw error;
            }
            // 4xx kliens hibáknál ne próbáld újra (401, 403, 404, stb.)
            if (error.status >= 400 && error.status < 500) {
              throw error;
            }
            // Exponenciális backoff: 1s, 2s, 4s
            return timer(Math.pow(2, retryCount - 1) * 1000);
          }
        }),
        catchError(this.handleError(callerStack))
      );
  }

  /**
   * Érvénytelen/lejárt token esetén tisztítja a localStorage-t és átirányít
   * a /login oldalra. Idempotens: egyszerre csak egy redirect indul.
   */
  protected handleMissingToken(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.isRedirectingToLogin) return;

    // Ha már a login oldalon vagyunk, ne csináljunk semmit
    const currentUrl = this.router.url || '';
    if (currentUrl.startsWith('/login')) return;

    this.isRedirectingToLogin = true;
    console.warn('Érvénytelen/lejárt token - átirányítás a login oldalra');

    try {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem('userData');
    } catch {
      // localStorage nem elérhető - figyelmen kívül hagyjuk
    }

    this.router.navigate(['/login'], {
      queryParams: {
        reason: 'session_expired',
        message: 'A munkamenet lejárt, kérlek jelentkezz be újra.'
      }
    }).finally(() => {
      this.isRedirectingToLogin = false;
    });
  }

  /**
   * Returns an observable that emits an empty array when no token is available
   */
  protected returnEmptyArrayWhenNoToken<T>(): Observable<T[]> {
    this.handleMissingToken();
    return new Observable(observer => {
      observer.next([]);
      observer.complete();
    });
  }

  /**
   * Returns an observable with error response when no token is available
   */
  protected returnErrorWhenNoToken(message: string = 'Nincs érvényes bejelentkezés!'): Observable<any> {
    this.handleMissingToken();
    return new Observable(observer => {
      observer.next({ success: false, message });
      observer.complete();
    });
  }

  /**
   * Logs API call information for debugging
   */
  protected logApiCall(method: string, endpoint: string, data?: any): void {
    if (console && console.log) {
      const token = this.getToken();
      console.log(`${method} ${endpoint}`, {
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 50) + '...' : 'none',
        data: data || 'none'
      });
    }
  }
}